import type { CommonEditorTab } from "../editor/reducers";
import { useParams } from "react-router";
import { Fragment, useReducer } from "react";
import { usePageScreenSuspenseQuery } from "../client/generated";
import { editorReducer, inferTabIdentifier } from "../editor/reducers";
import { assertTypeName } from "../client/guards";
import { Sidebar } from "../shell/Sidebar";
import { Scaffold } from "../shell/Scaffold";
import { NavbarMetadataTabButton } from "../editor/NavbarMetadataTabButton";
import { NavbarMetadataTabContent } from "../editor/NavbarMetadataTabContent";
import { NavbarRegionsTabButton } from "../editor/NavbarRegionsTabButton";
import { NavbarRegionsTabContent } from "../editor/NavbarRegionsTabContent";
import { NavbarPagesTabButton } from "../editor/NavbarPagesTabButton";
import { NavbarPagesTabContent } from "../editor/NavbarPagesTabContent";
import { NavbarOverviewButton } from "../editor/NavbarOverviewButton";
import { SidebarDivider } from "../shell/SidebarDivider";
import { PageEditor } from "../editor/PageEditor";
import { NavbarPreviousPageButton } from "../editor/NavbarPreviousPageButton";
import { NavbarNextPageButton } from "../editor/NavbarNextPageButton";
import { Navbar } from "../shell/Navbar";
import { HybridRegionCreationTab } from "../types/hybrid/HybridRegionCreationTab";
import { RegionUpdateTab } from "../types/RegionUpdateTab";
import { HeadingRegionCreationTab } from "../types/heading/HeadingRegionCreationTab";
import { ArtifactRegionCreationTab } from "../types/artifact/ArtifactRegionCreationTab";
import { CaptionRegionCreationTab } from "../types/caption/CaptionRegionCreationTab";
import { FigureRegionCreationTab } from "../types/figure/FigureRegionCreationTab";
import { FormulaRegionCreationTab } from "../types/formula/FormulaRegionCreationTab";
import { ImageRegionCreationTab } from "../types/image/ImageRegionCreationTab";
import { ListRegionCreationTab } from "../types/list/ListRegionCreationTab";
import { TableRegionCreationTab } from "../types/table/TableRegionCreationTab";
import { TextRegionCreationTab } from "../types/text/TextRegionCreationTab";
import { TOCRegionCreationTab } from "../types/toc/TOCRegionCreationTab";
import { ListItemRegionCreationTab } from "../types/list-item/ListItemRegionCreationTab";
import { SublistRegionCreationTab } from "../types/sublist/SublistRegionCreationTab";
import { makePageThumbnailID } from "../regions/utils";
import { NavbarExportTabButton } from "../editor/NavbarExportTabButton";
import { NavbarExportTabContent } from "../editor/NavbarExportTabContent";

export function PageScreen() {
  const [state, dispatch] = useReducer(editorReducer, {
    mode: "rectangle",
    tab: "regions",
    table: undefined,
  });

  const { pageId } = useParams<{ pageId: string }>();

  const { data } = usePageScreenSuspenseQuery({
    variables: { pageId: pageId! },
  });

  const { page } = data;
  assertTypeName(page?.__typename === "Page");

  const switchTab = (newTab: CommonEditorTab) => {
    dispatch({
      type: "switchToCommonTab",
      tab: newTab,
    });

    setTimeout(() => {
      document.getElementById(makePageThumbnailID(page.id))?.scrollIntoView({
        behavior: "instant",
        block: "center",
      });
    });
  };

  return (
    <Scaffold
      tabsValue={inferTabIdentifier(state)}
      onTabsValueChange={(newTab) => switchTab(newTab as CommonEditorTab)}
      navbar={
        <Navbar>
          <NavbarOverviewButton fragment={page.document} />
          <NavbarPreviousPageButton fragment={page} />
          <NavbarNextPageButton fragment={page} />
          <SidebarDivider />
          <NavbarMetadataTabButton />
          <NavbarPagesTabButton />
          <NavbarRegionsTabButton />
          <NavbarExportTabButton />
        </Navbar>
      }
      sidebar={
        <Sidebar>
          <NavbarMetadataTabContent fragment={page.document} />
          <NavbarPagesTabContent fragment={page} />
          <NavbarRegionsTabContent
            state={state}
            dispatch={dispatch}
            fragment={page}
          />

          {page.regions.map((region) => (
            <Fragment key={region.id}>
              <ArtifactRegionCreationTab
                dispatch={dispatch}
                fragment={region}
              />
              <CaptionRegionCreationTab dispatch={dispatch} fragment={region} />
              <FigureRegionCreationTab dispatch={dispatch} fragment={region} />
              <FormulaRegionCreationTab dispatch={dispatch} fragment={region} />
              <HeadingRegionCreationTab dispatch={dispatch} fragment={region} />
              <HybridRegionCreationTab dispatch={dispatch} fragment={region} />
              <ImageRegionCreationTab dispatch={dispatch} fragment={region} />
              <ListRegionCreationTab dispatch={dispatch} fragment={region} />
              <ListItemRegionCreationTab
                dispatch={dispatch}
                fragment={region}
              />
              <SublistRegionCreationTab dispatch={dispatch} fragment={region} />
              <TableRegionCreationTab
                dispatch={dispatch}
                state={state}
                fragment={region}
              />
              <TextRegionCreationTab dispatch={dispatch} fragment={region} />
              <TOCRegionCreationTab dispatch={dispatch} fragment={region} />
              <RegionUpdateTab
                dispatch={dispatch}
                state={state}
                fragment={region}
              />
            </Fragment>
          ))}
          <NavbarExportTabContent
            fragment={page.document}
            isActive={state.tab === "export"}
          />
        </Sidebar>
      }
    >
      <PageEditor fragment={page} state={state} dispatch={dispatch} />
    </Scaffold>
  );
}
